import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import useStore from '../store/useStore';
import { buildAssetUrl } from '../utils/api';
import WishlistButton from './Products/WishlistButton';


const getProductId = (product) => product.product_id ?? product.id;
const getProductName = (product) => product.name ?? product.product_name ?? 'Product';
const getCategoryName = (product) => product.category ?? product.category_name ?? 'Fresh Pick';

const getPrice = (value) => {
  const price = Number.parseFloat(value);
  return Number.isFinite(price) ? price : 0;
};

const getImage = (product) => (
  product.image ?? product.product_image ?? product.image1 ?? null
);

const getDiscount = (product) => {
  const price = getPrice(product.price);
  const originalPrice = getPrice(product.originalPrice ?? product.original_price);

  if (!originalPrice || originalPrice <= price) return 0;
  return Math.round(((originalPrice - price) / originalPrice) * 100);
};

const handleCardKeyDown = (event, onOpen) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onOpen();
  }
};

const ProductFallback = ({ compact = false }) => (
  <div className={`flex items-center justify-center rounded-2xl bg-white/70 text-primary-300 ${compact ? 'h-28 w-28' : 'h-36 w-36'}`}>
    <svg className={compact ? 'h-12 w-12' : 'h-16 w-16'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.4} d="M20 13c0 5-3.5 8-8 8s-8-3-8-8c0-4 3.5-8 8-10 4.5 2 8 6 8 10z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.4} d="M12 21V9m0 5 4-4m-4 4-4-4" />
    </svg>
  </div>
);

const AddButton = ({ product, adding, onAddToCart, variant = 'primary' }) => {
  const isLight = variant === 'light';

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onAddToCart(product);
      }}
      disabled={adding}
      className={
        isLight
          ? 'flex flex-1 items-center justify-center gap-2 rounded-2xl bg-white py-3.5 text-sm font-bold text-primary-700 shadow-lg transition-all hover:bg-primary-50 hover:shadow-xl active:scale-[0.98] disabled:cursor-wait disabled:opacity-60'
          : 'flex w-full items-center justify-center gap-2 rounded-2xl bg-primary-600 py-3 text-xs font-bold text-white shadow-md shadow-primary-200 transition-all hover:bg-primary-700 hover:shadow-lg active:scale-[0.98] disabled:cursor-wait disabled:opacity-60 sm:text-sm'
      }
    >
      {adding ? (
        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
      ) : (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13 5.4 5M7 13l-2 2c-.6.6-.2 2 .7 2H17m0 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-8 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z" />
        </svg>
      )}
      {adding ? 'Adding...' : 'Add to Cart'}
    </button>
  );
};

const HeroDealCard = ({ product, onAddToCart, adding }) => {
  const navigate = useNavigate();
  const [imgErr, setImgErr] = useState(false);
  const productId = getProductId(product);
  const productName = getProductName(product);
  const categoryName = getCategoryName(product);
  const price = getPrice(product.price);
  const originalPrice = getPrice(product.originalPrice ?? product.original_price);
  const discount = getDiscount(product);
  const image = getImage(product);
  const openProduct = () => navigate(`/product/${productId}`);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openProduct}
      onKeyDown={(event) => handleCardKeyDown(event, openProduct)}
      className="group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-3xl bg-primary-700 text-white shadow-xl shadow-primary-900/25 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl lg:flex-row"
    >
      <div className="absolute left-5 top-5 z-10 flex flex-col gap-2">
        <span className="w-fit rounded-full bg-amber-300 px-3 py-1 text-xs font-black uppercase tracking-wide text-gray-950 shadow-sm">
          Best Deal
        </span>
        {discount > 0 && (
          <span className="w-fit rounded-full bg-red-500 px-3 py-1 text-xs font-black text-white shadow-sm">
            {discount}% Off
          </span>
        )}
      </div>

      {/* Wishlist button — top right */}
      <div
        className="absolute right-4 top-4 z-20"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <WishlistButton productId={productId} size="sm" />
      </div>

      <div className="relative flex h-60 shrink-0 items-center justify-center overflow-hidden bg-primary-600/60 lg:h-auto lg:w-72">
        {image && !imgErr ? (
          <img
            src={buildAssetUrl(image)}
            alt={productName}
            className="h-44 w-44 object-contain drop-shadow-2xl transition-transform duration-500 group-hover:scale-105 lg:h-full lg:w-full lg:p-8"
            onError={() => setImgErr(true)}
          />
        ) : (
          <ProductFallback />
        )}
      </div>

      <div className="flex flex-1 flex-col justify-between p-6 lg:p-8">
        <div>
          <span className="text-xs font-bold uppercase tracking-widest text-primary-100">
            {categoryName}
          </span>
          <h3 className="mt-2 line-clamp-2 text-2xl font-black leading-tight lg:text-3xl">
            {productName}
          </h3>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-primary-100">
            Fresh quality, clean packing, and quick delivery from trusted local suppliers.
          </p>
        </div>

        <div className="mt-7">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-4xl font-black">Rs {price.toFixed(0)}</span>
            <span className="text-sm text-primary-100">/{product.unit || 'unit'}</span>
            {originalPrice > price && (
              <span className="text-base text-primary-200 line-through">Rs {originalPrice.toFixed(0)}</span>
            )}
          </div>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <AddButton product={product} adding={adding} onAddToCart={onAddToCart} variant="light" />
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                openProduct();
              }}
              className="flex-1 rounded-2xl border-2 border-white/40 py-3.5 text-sm font-bold text-white transition-all hover:bg-white/10"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SmallDealCard = ({ product, index, onAddToCart, adding }) => {
  const navigate = useNavigate();
  const [imgErr, setImgErr] = useState(false);
  const productId = getProductId(product);
  const productName = getProductName(product);
  const categoryName = getCategoryName(product);
  const price = getPrice(product.price);
  const originalPrice = getPrice(product.originalPrice ?? product.original_price);
  const discount = getDiscount(product);
  const image = getImage(product);
  const openProduct = () => navigate(`/product/${productId}`);
  const backgrounds = ['bg-amber-50', 'bg-sky-50', 'bg-rose-50'];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openProduct}
      onKeyDown={(event) => handleCardKeyDown(event, openProduct)}
      className="group relative flex cursor-pointer flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary-100 hover:shadow-xl"
    >
      {discount > 0 && (
        <div className="absolute left-3 top-3 z-10 rounded-full bg-red-500 px-2.5 py-1 text-[10px] font-black text-white shadow">
          {discount}% Off
        </div>
      )}

      {/* Wishlist button — top right */}
      <div
        className="absolute right-3 top-3 z-20 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <WishlistButton productId={productId} size="sm" />
      </div>

      <div className={`flex h-44 items-center justify-center overflow-hidden ${backgrounds[index % backgrounds.length]} p-5`}>
        {image && !imgErr ? (
          <img
            src={buildAssetUrl(image)}
            alt={productName}
            className="h-32 w-32 object-contain drop-shadow-md transition-transform duration-500 group-hover:scale-105"
            onError={() => setImgErr(true)}
          />
        ) : (
          <ProductFallback compact />
        )}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <span className="mb-1 truncate text-[10px] font-bold uppercase tracking-widest text-primary-600">
          {categoryName}
        </span>
        <h3 className="mb-3 line-clamp-2 text-sm font-bold leading-snug text-gray-900 transition-colors group-hover:text-primary-700 sm:text-base">
          {productName}
        </h3>

        <div className="mt-auto">
          <div className="mb-3 flex items-baseline gap-2">
            <span className="text-xl font-black text-gray-900">Rs {price.toFixed(0)}</span>
            <span className="text-xs text-gray-400">/{product.unit || 'unit'}</span>
            {originalPrice > price && (
              <span className="ml-auto text-xs text-gray-400 line-through">Rs {originalPrice.toFixed(0)}</span>
            )}
          </div>
          <AddButton product={product} adding={adding} onAddToCart={onAddToCart} />
        </div>
      </div>
    </div>
  );
};

const FeaturedDeals = () => {
  const { products, products_loading, products_loaded, addToCart } = useStore();
  const navigate = useNavigate();
  const [addingId, setAddingId] = useState(null);

  const topDeals = [...products]
    .filter((product) => getProductId(product) && getPrice(product.price) > 0)
    .sort((a, b) => {
      const discountDiff = getDiscount(b) - getDiscount(a);
      if (discountDiff !== 0) return discountDiff;
      return getPrice(a.price) - getPrice(b.price);
    })
    .slice(0, 4);

  // Show skeleton while products are loading
  if (products_loading || !products_loaded) {
    return (
      <section className="bg-white py-5 sm:py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <div className="h-4 w-28 animate-pulse rounded-full bg-amber-100" />
            <div className="mt-4 h-8 w-56 animate-pulse rounded-full bg-gray-200" />
            <div className="mt-2 h-4 w-72 animate-pulse rounded-full bg-gray-100" />
          </div>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
            <div className="animate-pulse rounded-3xl bg-primary-100 lg:col-span-2" style={{ minHeight: 280 }} />
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse rounded-3xl border border-gray-100 bg-white shadow-sm" style={{ minHeight: 280 }}>
                <div className="h-44 rounded-t-3xl bg-gray-200" />
                <div className="space-y-2 p-4">
                  <div className="h-3 w-20 rounded-full bg-gray-200" />
                  <div className="h-4 w-full rounded-full bg-gray-200" />
                  <div className="h-9 rounded-xl bg-gray-200" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (topDeals.length === 0) return null;

  const handleAddToCart = async (product) => {
    const productId = getProductId(product);
    if (!productId) return;
    if (addingId === productId) return; // guard: ignore rapid clicks while request is in-flight
    setAddingId(productId);
    try {
      await addToCart(product, 1);
      // Use a stable toast ID so repeated quick clicks never stack multiple toasts
      toast.success(`${getProductName(product)} added to cart`, {
        id: `add-cart-${productId}`,
      });
    } finally {
      setAddingId(null);
    }
  };

  const [hero, ...rest] = topDeals;

  return (
    <section className="bg-white py-5 sm:py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-100 bg-amber-50 px-4 py-2 text-xs font-bold uppercase tracking-widest text-amber-700">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Hot Deals
            </span>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-gray-900 sm:text-4xl">
              Featured savings
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-gray-500">
              Quick picks with sharp prices from today&apos;s fresh inventory.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate('/products')}
            className="group inline-flex items-center gap-2 self-start rounded-full border-2 border-gray-200 bg-white px-6 py-3 text-sm font-bold text-gray-700 shadow-sm transition-all hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 sm:self-auto"
          >
            Shop All
            <svg className="h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0-7 7m7-7H3" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <HeroDealCard
              product={hero}
              onAddToCart={handleAddToCart}
              adding={addingId === getProductId(hero)}
            />
          </div>

          {rest.map((product, index) => (
            <SmallDealCard
              key={getProductId(product)}
              product={product}
              index={index}
              onAddToCart={handleAddToCart}
              adding={addingId === getProductId(product)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedDeals;
